// Der Begleiter: ein Kasten über jedem Schritt, der erklärt statt zu fordern
// — wie im Event Manager. Er lässt sich zuklappen; wer den Ablauf kennt,
// soll nicht scrollen müssen.
import { useState } from 'react';
import { ArrowRight, ChevronDown, ChevronUp, HelpingHand } from 'lucide-react';
import type { SchrittId } from './modell';
import './Begleiter.css';

type Text = { hallo: string; warum: string[]; danach?: string };

const TEXTE: Record<SchrittId, Text> = {
  vorlage: {
    hallo: 'Worum geht es in Deiner Mail? Wähl die Vorlage, die am besten passt. Jede ist einer Mail nachgebaut, die schon rausging — Aufbau, Farben und Pflichtangaben stimmen von selbst.',
    warum: [
      'Mit der Vorlage steht fest, welche Fragen vorab kommen und welche Felder Du ausfüllst.',
      'Passt keine, nimm „Allgemeine Info". Eine Botschaft pro Mail — lieber zwei kurze als eine lange.',
    ],
    danach: 'Als Nächstes: ein paar Fragen, die vorher geklärt sein müssen.',
  },
  fragen: {
    hallo: 'Bevor Du schreibst, klären wir, was vorher stehen muss. Unter jeder Frage steht, warum wir fragen.',
    warum: [
      'Was in der Mail steht, muss am Tresen, im CRM, am Terminal und auf der Website genauso stimmen.',
      'Ist etwas noch nicht geklärt, kannst Du weiterarbeiten — einreichen geht aber erst, wenn es geklärt ist.',
      'Die Redaktion sichtet montags bis freitags und braucht vier volle Werktage. Ist es knapper, markierst Du die Mail als „eilt" und begründest es — bis 10 Uhr eingereicht, geht sie noch am selben Tag raus.',
    ],
    danach: 'Als Nächstes: der Inhalt der Mail.',
  },
  inhalt: {
    hallo: 'Füll die Felder aus — rechts siehst Du sofort die fertige Mail, genau so, wie sie ankommt.',
    warum: [
      'Pflichtfelder sind mit * markiert. Was leer bleibt, erscheint in der Mail nicht.',
      'Die Knöpfe über dem Textfeld machen Text fett, kursiv, markiert, farbig oder größer — sparsam eingesetzt, hebt das das Wichtigste hervor. Eine Leerzeile beginnt einen neuen Absatz.',
      'Unter „Gestaltung" kommen Bannerbild, Symbol im Kopf und die Menge an Emojis dazu.',
      'Für Betreff und Vorschautext gibt es Vorschläge — nimm einen oder schreib Deinen eigenen.',
    ],
    danach: 'Als Nächstes: wer die Mail bekommt.',
  },
  empfaenger: {
    hallo: 'Wer soll die Mail bekommen? Wähl eine oder mehrere Gruppen — sie sind schon auf Deine Sportschule gefiltert.',
    warum: [
      'Es bekommen nur Kontakte mit Einwilligung die Mail; Abmeldungen und unzustellbare Adressen fallen von selbst heraus.',
      'Wer in mehreren Gruppen steht, bekommt die Mail nur einmal. Bei Kindern geht sie an das Postfach der Eltern.',
      'Die internen Verteiler bekommen jede Mail in Kopie — so wissen Tresen, Studioleitungen und Redaktion Bescheid.',
    ],
    danach: 'Als Nächstes: prüfen, Testmail, einreichen.',
  },
  pruefen: {
    hallo: 'Fast geschafft. Der Assistent prüft alles noch einmal. Schick Dir eine Testmail und schau sie Dir auch auf dem Handy an.',
    warum: [
      'Erst mit dem Einreichen sieht die Redaktion die Mail. Sie prüft Sprache, Form und Empfänger und gibt frei — oder fragt nach.',
      'Du bekommst bei Freigabe oder Rückfrage eine E-Mail. Den Stand siehst Du jederzeit unter „Meine Mails".',
      'Solange die Mail nicht freigegeben ist, kannst Du sie zurückziehen und ändern.',
    ],
  },
};

/** Letzter Schritt für alle, die ohne Freigabe versenden dürfen. */
const PRUEFEN_SELBST: Text = {
  hallo: 'Fast geschafft. Du darfst ohne Freigabe versenden: Prüf die Mail, schick Dir eine Testmail und bestätige dann den letzten Check.',
  warum: [
    'Du wählst, ob die Mail sofort oder zum Wunschtermin rausgeht.',
    'Im letzten Check hakst Du ab, dass Empfänger, Betreff, Rechtschreibung, Links und Absender stimmen. Danach geht die Mail an Brevo.',
    'Unsicher? „Stattdessen zur Freigabe geben" schickt sie wie gewohnt an die Redaktion.',
  ],
};

export function Begleiter({ schritt, selbst = false }: { schritt: SchrittId; selbst?: boolean }) {
  const [zu, setZu] = useState<boolean>(() => {
    try { return localStorage.getItem('mail-begleiter-zu') === 'ja'; } catch { return false; }
  });
  const text = selbst && schritt === 'pruefen' ? PRUEFEN_SELBST : TEXTE[schritt];

  const umschalten = () => {
    const neu = !zu;
    setZu(neu);
    try { localStorage.setItem('mail-begleiter-zu', neu ? 'ja' : 'nein'); } catch { /* egal */ }
  };

  return (
    <aside className={`begleiter${zu ? ' begleiter--zu' : ''}`} aria-label="Was jetzt zu tun ist" data-tour="assistent-begleiter">
      <button type="button" className="begleiter-kopf" onClick={umschalten} aria-expanded={!zu}>
        <HelpingHand size={17} aria-hidden="true" />
        <span className="begleiter-titel">Was jetzt zu tun ist</span>
        {zu ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronUp size={16} aria-hidden="true" />}
      </button>
      {!zu && (
        <div className="begleiter-inhalt">
          <p className="begleiter-hallo">{text.hallo}</p>
          <p className="begleiter-label">Gut zu wissen</p>
          <ul className="begleiter-liste">
            {text.warum.map((z, i) => <li key={i}>{z}</li>)}
          </ul>
          {text.danach && (
            <p className="begleiter-danach"><ArrowRight size={14} aria-hidden="true" /> <span>{text.danach}</span></p>
          )}
        </div>
      )}
    </aside>
  );
}
